import {
	BasicButton,
	BasicModal,
	ButtonFloatingSubmit,
	Typography,
} from "@/components";
import { BackAppBar, GalleryImage } from "@/components/ui";
import { useBoolean } from "@/hooks/useBoolean";
import useProfile from "@/hooks/useProfile";
import { QUERY_KEYS } from "@/libraries/constant";
import { storageService } from "@/services";
import { useStorage } from "@/stores";
import type { ResError } from "@/types";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import UploadOutlinedIcon from "@mui/icons-material/UploadOutlined";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import { useMutation } from "@tanstack/react-query";
import { Link, createFileRoute, redirect } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { toast } from "sonner";

export const Route = createFileRoute("/dashboard/gallery/exports")({
	beforeLoad: () => {
		const { storages } = useStorage.getState();
		if (!storages.length) {
			throw redirect({
				to: "/dashboard/gallery",
			});
		}
	},
	component: RouteComponent,
});

function RouteComponent() {
	const navigate = Route.useNavigate();
	const {
		value: isOpenConfirm,
		setTrue: openConfirm,
		setFalse: closeConfirm,
	} = useBoolean();
	const [imageIdx, setImageIdx] = useState<number>(0);
	const { profile } = useProfile();
	const storages = useStorage((state) => state.storages);
	const removeStorage = useStorage((state) => state.removeStorage);
	const resetStorages = useStorage((state) => state.resetStorages);
	const exportMutation = useMutation<unknown, ResError, number[]>({
		mutationKey: [QUERY_KEYS.MUTATE_EXPORT_IMAGES],
		mutationFn: (ids) => storageService.exports(ids),
	});
	const image = useMemo(() => storages[imageIdx], [storages, imageIdx]);
	const onExportImages = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		const ids = storages.map((storage) => storage.id);
		return exportMutation.mutate(ids, {
			onSuccess: () => {
				resetStorages();
				toast.success("Files exported and sent to email successfully");
				return navigate({
					to: "/dashboard/gallery",
					replace: true,
				});
			},
			onError: () => {
				toast.error("Failed to export images");
			},
		});
	};
	const onClickRemove = (
		e: React.MouseEvent<HTMLButtonElement>,
		imgIdx: number,
	) => {
		e.stopPropagation();
		e.preventDefault();
		if (!storages[imgIdx]) return;
		return removeStorage(storages[imgIdx]).then((currLength) => {
			if (!currLength) {
				return navigate({
					to: "/dashboard/gallery",
					replace: true,
				});
			}
		});
	};
	const onViewImage = (e: React.MouseEvent<HTMLDivElement>, imgIdx: number) => {
		e.stopPropagation();
		return setImageIdx(imgIdx);
	};
	return (
		<div className="relative flex flex-col h-full overflow-hidden">
			<BackAppBar
				beforeBack={resetStorages}
				to="/dashboard/gallery"
				title="Export Files"
			/>
			<div className="flex-1 flex flex-col overflow-y-auto">
				<div className="container">
					<div className="relative aspect-[3/2] bg-dark">
						{image ? (
							<img
								src={image.file_id}
								alt={image.file_id}
								className="w-full h-full object-contain object-center"
							/>
						) : null}
					</div>
				</div>
				<div className="flex-none container overflow-x-auto py-4">
					<div className="flex flex-row gap-4 [&_div]:w-40 [&_div]:flex-shrink-0 [&_a]:w-40 [&_a]:flex-shrink-0">
						{storages.map((storage, storageIdx) => (
							<GalleryImage
								key={storage.file_id}
								imgSrc={storage.file_id}
								imgAlt={storage.file_id}
								isActive={storageIdx === imageIdx}
								onClickRemove={(e) => onClickRemove(e, storageIdx)}
								onViewImage={(e) => onViewImage(e, storageIdx)}
							/>
						))}
						<Link
							to="/dashboard/gallery"
							search={{ export: true }}
							className="relative aspect-[3/2] overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300 ease-in-out border-2 border-dashed border-secondary flex justify-center items-center"
						>
							<IconButton
								size="large"
								color="primary"
								className="!bg-secondary"
							>
								<AddIcon className="text-blue-950" />
							</IconButton>
						</Link>
					</div>
				</div>
				{profile ? (
					<>
						<Divider />
						<div className="container py-4">
							<Typography>The files will be sent to your emai:</Typography>
							<Typography fontWeight={500}>{profile.email}</Typography>
						</div>
					</>
				) : null}
			</div>
			<div className="container py-4 shadow">
				<BasicButton
					fullWidth
					variant="contained"
					color="secondary"
					className="w-full !h-12 text-sm font-semibold !shadow-none"
					onClick={openConfirm}
				>
					{`Export (${storages.length})`}
				</BasicButton>
			</div>
			<BasicModal isOpen={isOpenConfirm} onClose={closeConfirm}>
				<IconButton
					color="primary"
					size="large"
					className="!absolute top-2 right-2"
					onClick={closeConfirm}
				>
					<CloseIcon />
				</IconButton>
				<div className="flex flex-col justify-center items-center pt-8 gap-3">
					<div className="w-fit p-6 bg-primary-light rounded-[3rem]">
						<UploadOutlinedIcon className="!text-primary !text-[5rem]" />
					</div>
					<Typography fontWeight={600} fontSize={16}>
						Export
					</Typography>
					<Typography align="center">
						Users of HEMI Teams app are responsible for the appropriate use of
						images as determined by their professional duty and workplace.
					</Typography>
					<Button
						variant="contained"
						color="primary"
						size="large"
						className="!rounded-full"
						fullWidth
						onClick={onExportImages}
						disabled={exportMutation.isPending}
					>
						Export
					</Button>
					<Button
						variant="outlined"
						color="primary"
						size="large"
						className="!rounded-full"
						fullWidth
						onClick={closeConfirm}
					>
						Cancel
					</Button>
				</div>
			</BasicModal>
		</div>
	);
}
